import axios from "axios";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Share2, Users } from "lucide-react";
import { DEFAULT_AVATAR } from "../../../../../utils/fallbackImages";

export default function MyShareAsset() {
  const [expanded, setExpanded] = useState([]);
  const [myshareData, setMyShareData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const session_id = Cookies.get("jwt");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    axios
      .post(
        `${process.env.REACT_APP_API}/mlm/myshare`,
        { get: "true" },
        { headers: { Authorization: `Bearer ${session_id}` } }
      )
      .then((res) => setMyShareData(res.data.myshare))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  function handleDropDownListMember(index) {
    setExpanded((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }

  const totalShares = myshareData.reduce((acc, d) => acc + d.sharee.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-4 lg:pt-24">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3"
        >
          <Link to="/profiles">
            <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-gray-100 transition-colors flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </Link>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">โปรไฟล์</p>
            <h1 className="text-lg font-bold text-gray-900">การแชร์ทรัพย์</h1>
          </div>
        </motion.div>

        {/* ── Stats ── */}
        {!isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.04 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Share2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">ทรัพย์ที่แชร์</p>
                <p className="text-2xl font-bold text-gray-900">{myshareData.length}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">การแชร์ต่อทั้งหมด</p>
                <p className="text-2xl font-bold text-gray-900">{totalShares}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Loading skeleton ── */}
        {isLoading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-100" />
                <div className="px-5 py-4 space-y-3">
                  <div className="h-4 bg-gray-100 rounded-full w-1/3" />
                  <div className="h-10 bg-gray-100 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!isLoading && myshareData.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-20 gap-4"
          >
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100">
              <Share2 className="w-7 h-7 text-gray-300" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-700">ยังไม่มีทรัพย์ที่แชร์</p>
              <p className="text-xs text-gray-400 mt-1">ทรัพย์ที่คุณแชร์จะแสดงที่นี่</p>
            </div>
          </motion.div>
        )}

        {/* ── Share list ── */}
        {!isLoading && myshareData.length > 0 && (
          <div className="space-y-3">
            {myshareData.map((data, idxMain) => {
              const Lead1st = data.sharee[0];
              const another_shared = data.sharee;

              return (
                <motion.div
                  key={idxMain}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idxMain * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  {/* Asset image */}
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img src={data.asset_image} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                        ทรัพย์ที่แชร์
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                        {another_shared.length} การแชร์ต่อ
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="px-5 py-4 space-y-3">

                    {/* Leader */}
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-amber-100">
                        <img src="https://cdn-icons-png.flaticon.com/512/1021/1021218.png" alt="" className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">ต้นสาย</p>
                        <p className="text-sm font-semibold text-gray-800">{Lead1st ? Lead1st.uname : "ไม่พบ"}</p>
                      </div>
                    </div>

                    {/* Expand toggle */}
                    <button
                      className="w-full flex items-center justify-between py-2.5 px-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors"
                      onClick={() => handleDropDownListMember(idxMain)}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm font-semibold text-gray-700">ดูรายชื่อผู้แชร์ต่อ</span>
                      </div>
                      <motion.div
                        animate={{ rotate: expanded[idxMain] ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </motion.div>
                    </button>

                    {/* Member list */}
                    <AnimatePresence>
                      {expanded[idxMain] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-1.5 max-h-52 overflow-y-auto pt-1">
                            {another_shared.map((member, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100"
                              >
                                <img
                                  src={member.profile_image}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0"
                                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate">{member.uname}</p>
                                </div>
                                <span className="text-xs text-gray-400 font-mono">#{idx + 1}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
